import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { OverallResponseItem } from "@/interfaces/overall-response.interface";
import { cn } from "@/lib/utils";

/**
 * Props for the OverallResponseChart component
 */
interface OverallResponseChartProps {
  /** Array of overall response items to chart */
  data: OverallResponseItem[];
  /** Additional CSS classes */
  className?: string;
}

/**
 * OverallResponseChart component displays a bar chart showing overall response
 * metrics (accuracy, factuality, relevance, faithfulness, coherence) using Recharts.
 * Shows custom labels with values on bars. Returns null if no data.
 * 
 * @component
 * @param {OverallResponseChartProps} props - Component props
 * @param {OverallResponseItem[]} props.data - Response items to chart
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element | null} Bar chart or null if no data
 * 
 * @example
 * ```tsx
 * <OverallResponseChart data={responseItems} />
 * ```
 */

const COLORS = {
  accuracy: "#799A3E",
  factuality: "#6F42C1",
  relevance: "#DC3545",
  faithfulness: "#FF7518",
  coherence: "#0D6EFD",
};

const CustomLabel = (props: any) => {
  const { x, y, width, value, height } = props;
  if (!value || value === 0) return null;
  
  const labelY = y + height - 10;
  
  return (
    <text
      x={x + width / 2}
      y={labelY}
      fill="#171c26"
      textAnchor="middle"
      fontSize={12}
      fontWeight={500}
      letterSpacing="1px"
    >
      {value.toFixed(1)}
    </text>
  );
};

export const OverallResponseChart: React.FC<OverallResponseChartProps> = ({
  data,
  className,
}) => {
  const chartData = data.map((item) => ({
    name: item.corpus.charAt(0).toUpperCase() + item.corpus.slice(1),
    Clarity: item.clarity,
    "Answer Relevancy": item["a relevancy"],
    "Context Relevancey": item["c relevancy"],
    Faithfulness: item.faithfulness,
    Coherence: item.coherence,
  }));

  return (
    <div className={cn("bg-background rounded-md border border-border p-4 overall-response-chart", className)}>
      <div className="w-full max-w-[1200px]">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 50, left: 5, bottom: 0 }}
            barCategoryGap="20%"
            barSize={60}
            barGap={16}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
            <XAxis
              dataKey="name"
              tick={{ fill: "#171c26", fontSize: 12, fontWeight: 500 }}
              stroke="#E6E6E6"
            />
            <YAxis
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              tick={{ fill: "#171c26", fontSize: 12 }}
              stroke="#E6E6E6"
            />
            <Legend
              wrapperStyle={{ paddingTop: "20px" }}
              iconType="square"
              formatter={(value) => <span className="text-black/70">{value}</span>}
            />
            <Bar
              dataKey="Clarity"
              fill={COLORS.accuracy}
            >
              <LabelList content={<CustomLabel />} />
            </Bar>
            <Bar
              dataKey="Coherence"
              fill={COLORS.coherence}
            >
              <LabelList content={<CustomLabel />} />
            </Bar>
            <Bar
              dataKey="Answer Relevancy"
              fill={COLORS.factuality}
            >
              <LabelList content={<CustomLabel />} />
            </Bar>
            <Bar
              dataKey="Faithfulness"
              fill={COLORS.faithfulness}
            >
              <LabelList content={<CustomLabel />} />
            </Bar>
            <Bar
              dataKey="Context Relevancey"
              fill={COLORS.relevance}
            >
              <LabelList content={<CustomLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

